import { useCallback, useEffect, useState } from "react";
const DEFAULT_CONSENT_LOCAL_STORAGE_KEY = "cookie-consent";
export const useCookieConsent = ({ localStorageKey, initialState, } = {}) => {
    const parseStorageState = useCallback(() => {
        // If running in non-browser environment, skip the parsing.
        if (typeof localStorage === "undefined") {
            return undefined;
        }
        const storageKey = localStorageKey ?? DEFAULT_CONSENT_LOCAL_STORAGE_KEY;
        try {
            if (!storageKey)
                return undefined;
            const storageState = localStorage.getItem(storageKey);
            if (!storageState)
                return undefined;
            return JSON.parse(storageState);
        }
        catch (_error) {
            console.warn(`
        Failed to parse consent state from local storage. Used key: ${storageKey}
      `.trim());
        }
    }, [localStorageKey]);
    const getState = useCallback(() => {
        const parsedState = parseStorageState();
        return initialState || parsedState
            ? {
                ...initialState,
                ...parsedState,
            }
            : undefined;
    }, [initialState, parseStorageState]);
    const [consentState, _setConsentState] = useState(getState());
    useEffect(() => {
        if (!consentState) {
            _setConsentState(getState());
        }
    }, []);
    const setConsentState = useCallback((consent) => {
        const storageKey = localStorageKey ?? DEFAULT_CONSENT_LOCAL_STORAGE_KEY;
        if (!consent) {
            localStorage.removeItem(storageKey);
            _setConsentState(undefined);
            return;
        }
        try {
            localStorage.setItem(storageKey, JSON.stringify(consent));
            _setConsentState(consent);
        }
        catch (error) {
            console.error(`Failed to save consent state.`, error);
        }
    }, [localStorageKey]);
    const update = useCallback((consent) => {
        self.gtag?.("consent", "update", {
            ...(consent?.["gtag.ad"] ? { ad_storage: consent["gtag.ad"] } : {}),
            ...(consent?.["gtag.analytics"]
                ? { analytics_storage: consent["gtag.analytics"] }
                : {}),
        });
        if (consent?.["fb-pixel"]) {
            self.fbq?.("consent", consent["fb-pixel"] === "granted" ? "grant" : "revoke");
        }
        setConsentState(consent);
    }, []);
    return [update, consentState];
};
/**
 * This hook returns <script> element content as a string. You can pass it as
 * `dangerouslySetInnerHTML` into the <script> element, or as props to your
 * script wrapper component.
 *
 * Code in this script must be called on every page of your site before any
 * commands that send measurement data (such as config or event) are executed.
 *
 * By default, all storage options are set to `denied`. You can change this
 * behavior via parameters.
 */
export const useDefaultConsentScriptContent = (params) => {
    const gtagAdStorageConsent = params?.["gtag.ad"] ?? "denied";
    const gtagAnalyticsStorageConsent = params?.["gtag.analytics"] ?? "denied";
    const gtagOptions = {
        region: params?.region,
        wait_for_update: params?.waitForUpdate,
    };
    const gtagStorage = {
        ad_storage: gtagAdStorageConsent,
        analytics_storage: gtagAnalyticsStorageConsent,
    };
    const fbPixelConsent = params?.["fb-pixel"] === "granted" ? "grant" : "revoke";
    const gtagScript = Object.entries(gtagStorage)
        .map(([consentKey, consentValue]) => `
      gtag('consent', 'default', {
        '${consentKey}': '${consentValue}',${Object.entries(gtagOptions)
        .filter(([, value]) => value)
        .map(([optionKey, optionValue]) => `'${optionKey}': ${typeof optionValue === "string" ? `'${optionValue}'` : optionValue},`)
        .join("\n")}      
      });`)
        .join("\n");
    return `
    window.dataLayer = window.dataLayer || [];
    window.queue = window.queue || [];
    
    function gtag(){
      dataLayer.push(arguments);
    }

    function fb() {
      queue.push(arguments);
    };

    ${gtagScript} 

    fb("consent", "${fbPixelConsent}");
  `.trim();
};
